import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Button } from "./button";
import { formatDateTime, formatInitials } from "@tellescope/utils";
import { Copy, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";

export interface AIChatMessageProps {
  /**
   * The role of the message sender
   */
  role: "user" | "assistant";
  /**
   * The message content to display
   */
  content: string;
  /**
   * The timestamp of the message
   */
  timestamp: Date;
  /**
   * Information about the sender (for user messages)
   */
  sender?: {
    name: string;
    avatar?: string;
  };
  /**
   * Callback when copy button is clicked
   */
  onCopy?: () => void;
  /**
   * Callback when feedback is provided (for user messages)
   */
  onFeedback?: (feedback: "positive" | "negative") => void;
  /**
   * Current feedback state
   */
  feedback?: "positive" | "negative";
  /**
   * Optional additional className for the container
   */
  className?: string;
}

/**
 * AIChatMessage - A message component for AI chat interfaces
 *
 * Displays messages differently based on role:
 * - User messages: Shows avatar with initials, message text, and feedback/copy actions
 * - Assistant messages: Shows sparkle icon, response text, and copy action
 *
 * @example
 * ```tsx
 * <AIChatMessage
 *   role="user"
 *   content="What is the weather today?"
 *   timestamp={new Date()}
 *   sender={{ name: "John Doe" }}
 *   onCopy={() => navigator.clipboard.writeText("What is the weather today?")}
 *   onFeedback={(feedback) => console.log(feedback)}
 * />
 * ```
 */
export const AIChatMessage = ({
  role,
  content,
  timestamp,
  sender,
  onCopy,
  onFeedback,
  feedback,
  className,
}: AIChatMessageProps) => {
  const { t } = useTranslation("ai");
  const isUser = role === "user";
  const formattedTimestamp = formatDateTime(timestamp, "hh:mm a");

  return (
    <div className={cn("flex gap-3 py-4", className)}>
      {/* Avatar / Icon */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar className="w-8 h-8">
            <AvatarImage src={sender?.avatar} alt={sender?.name} />
            <AvatarFallback useStaticBackground={true} id={sender?.name || "user"} className="text-xs">
              {sender?.name ? formatInitials(sender.name) : "U"}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">{isUser ? sender?.name || t("You") : t("AI Assistant")}</span>
          <span className="text-xs text-muted-foreground">{formattedTimestamp}</span>
        </div>

        <div className="text-sm text-foreground whitespace-pre-wrap">{content}</div>

        {/* Actions */}
        <div className="flex items-center gap-1 mt-2">
          {isUser && onFeedback && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onFeedback("positive")}
                hoverLabel={t("Good response")}
              >
                <ThumbsUp className={cn("h-3.5 w-3.5", feedback === "positive" ? "text-primary fill-primary" : "")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onFeedback("negative")}
                hoverLabel={t("Poor response")}
              >
                <ThumbsDown
                  className={cn("h-3.5 w-3.5", feedback === "negative" ? "text-destructive fill-destructive" : "")}
                />
              </Button>
            </>
          )}
          {onCopy && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCopy} hoverLabel={t("Copy message")}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
